import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Container } from '@/components/Container';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Row } from '@/components/Row';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import { getRecommendPrice, getWarranty } from '@/lib/service/api';
import type { RecommendPrice } from '@/lib/service/type';
import { isWatchSku, isWatchSkuFirstSix } from '@/lib/util';
import { commonMessages } from '@/messages/common';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';

import { FormSearch } from '../FormSearch';
import { PriceTable } from '../PriceTable';
import type { ServicePricesSearchSchema } from './schema';
import { servicePricesSearchSchema } from './schema';

import ReferenceHint from '@/assets/reference-and-serial-hint.png';

import * as S from './ServicePricesSearch.styles';

export const ServicePricesSearch = (
  props: ContentfulPlaceholderServiceFragment,
) => {
  const { country, locale } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpenWatchModal, setIsOpenWatchModal] = useState(false);
  const [productName, setProductName] = useState<string>();
  const [recommendPrice, setRecommendPrice] = useState<RecommendPrice[]>([]);
  const [searchNumber, setSearchNumber] = useState('');
  const [vatIncluded, setVatIncluded] = useState(false);
  const [packShipIncluded, setPackShipIncluded] = useState(false);
  const schema = servicePricesSearchSchema(formatMessage);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    init();
  }, []);
  const init = async () => {
    setIsInitializing(true);
    await initRecommendPrices();
    setIsInitializing(false);
  };

  const initRecommendPrices = async (sku = '') => {
    try {
      const res = await getRecommendPrice({
        locale: `${locale.slug}_${country.slug.toUpperCase()}`,
        watchReference: sku,
        countryCode: country.code,
      });
      setProductName(res?.model);
      setRecommendPrice(res?.prices ?? []);
      setVatIncluded(!!res?.vatIncluded);
      setPackShipIncluded(!!res?.packShipIncluded);
    } catch (error) {
      setRecommendPrice([]);
      setProductName('');
      setVatIncluded(false);
      setPackShipIncluded(false);
    }
  };

  const handleSubmit = async (params: ServicePricesSearchSchema) => {
    try {
      setSearchNumber(params.number);
      setIsProcessing(true);
      let sku = params.number;
      const isSku =
        isWatchSkuFirstSix(params.number) || isWatchSku(params.number);
      if (!isSku) {
        const warrantyData = await getWarranty(params.number);
        if (warrantyData.data?.sku) {
          sku = warrantyData.data.sku;
        }
      }
      await initRecommendPrices(sku);
      setIsProcessing(false);
    } catch (error) {
      setRecommendPrice([]);
      setProductName('');
      setIsProcessing(false);
    }
  };

  const handleFindSnAndRn = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpenWatchModal(true);
  };

  return (
    <>
      <FormSearch data-testid='ServicePricesFormSearch' {...props}>
        <FormProvider
          name='servicePricesSearch'
          {...formCtx}
          onSubmit={handleSubmit}
        >
          <Row showRowGap>
            <Col sm={8}>
              <InputField
                colorVariant='grey'
                disabled={isProcessing || isInitializing}
                name='number'
                placeholder={formatMessage(serviceMessages.serialNumberOrSku)}
                required
                style={{ height: '51px' }}
              />
            </Col>
            <Col sm={4}>
              <Button
                disabled={isProcessing || isInitializing}
                isFullWidth
                isProcessing={isProcessing}
                type='submit'
              >
                {formatMessage(commonMessages.search)}
              </Button>
            </Col>
          </Row>
          <Typography color='primaryBlue' mt={3} textAlign='center'>
            <LinkWithLocale
              data-testid='ServiceStatusLink'
              href='#'
              onClick={handleFindSnAndRn}
            >
              {formatMessage(serviceMessages.findSnAndRnLink)}
            </LinkWithLocale>
          </Typography>
        </FormProvider>
      </FormSearch>
      <Visibility xsHidden={!(isProcessing || isInitializing)}>
        <Loader />
      </Visibility>
      <Visibility
        xsHidden={
          isProcessing || isInitializing || !searchNumber || !!productName
        }
      >
        <Container>
          <Typography
            mb={4}
            scale='h4'
            textAlign='center'
            textTransform='uppercase'
          >
            {formatMessage(serviceMessages.noWatchFind)}
          </Typography>
        </Container>
      </Visibility>
      <Visibility
        xsHidden={
          isProcessing || isInitializing || (!!searchNumber && !productName)
        }
      >
        <Container>
          <S.TableTitleWrapper>
            <Typography
              data-testid='tableTitle'
              scale='h4'
              textAlign='center'
              textTransform='uppercase'
            >
              {productName
                ? formatMessage(serviceMessages.servicePricesResult, {
                    productName,
                  })
                : formatMessage(serviceMessages.allMaintenanceServices)}
            </Typography>
            {!productName && (
              <Typography textAlign='center'>
                {formatMessage(serviceMessages.priceTableSubTitle)}
              </Typography>
            )}
          </S.TableTitleWrapper>
          <PriceTable
            packShipIncluded={packShipIncluded}
            prices={recommendPrice}
            vatIncluded={vatIncluded}
          ></PriceTable>
        </Container>
      </Visibility>
      <Modal
        isOpen={isOpenWatchModal}
        onClose={() => setIsOpenWatchModal(false)}
        width='520px'
      >
        <Modal.Header onClose={() => setIsOpenWatchModal(false)}></Modal.Header>
        <Modal.Body>
          <S.Image alt='' src={ReferenceHint} />
        </Modal.Body>
      </Modal>
    </>
  );
};

ServicePricesSearch.displayName = 'ServicePricesSearch';
